// AUTO-GENERATED SEED — review before trusting. Rename keys (index→list/detail),
// refine params, confirm caller flags, add opt-in response types.
import { responseType } from '@auctogon/core/api/registry';
import type { InvoiceDueCheckResponse } from '../../schemas/responses/invoice-due-check';
import type { InvoiceLateFeesResponse } from '../../schemas/responses/invoice-late-fees';
import type { NotificationDrainResponse } from '../../schemas/responses/notification-drain';
import type { PaymentsSweepResponse } from '../../schemas/responses/payments-sweep';
import type { UploadsJanitorResponse } from '../../schemas/responses/uploads-janitor';
import { defineAdminRoute } from './define-admin-route';

// Declared, not bound (BL-1833 PR 4a): Cloud Scheduler calls these with no
// staff session, so the gate's 401 would stop every job. Each handler checks
// its own machine credential through authorizeCronRequest (BL-2600): a bearer
// secret or an OIDC token minted as its caller's service account. None of
// them acts for one tenant.
const CRON_TENANT = { from: 'none', reason: 'cron — sweeps every tenant' } as const;
const CRON = {
  grant: { none: 'cron — the CRON_SECRET bearer or a cron-invoker OIDC token authorizes it' },
  tenant: CRON_TENANT,
} as const;

export const cron = {
  // BL-1969: overdue_check, reap_payment_reservations and finix_payout_reconcile
  // retired with the old payments back office. late_fees returned with BL-2097.
  auction_reminders: defineAdminRoute({
      path: '/api/cron/auction-reminders',
      params: {},
      methods: { GET: CRON, POST: CRON },
      caller: 'cron',
    }),
  // BL-2044: hourly, so each invoice is checked in the first tick after
  // midnight in its own zone; writes only the Past due Activity line.
  invoice_due_check: defineAdminRoute({
      path: '/api/cron/invoice-due-check',
      params: {},
      methods: { GET: { ...CRON, response: responseType<InvoiceDueCheckResponse>() } },
      caller: 'cron',
    }),
  // BL-2097: hourly, so each invoice's late fee lands in the first tick after
  // midnight in its own zone; adds fees as new versions and may email them.
  late_fees: defineAdminRoute({
      path: '/api/cron/late-fees',
      params: {},
      methods: { GET: { ...CRON, response: responseType<InvoiceLateFeesResponse>() } },
      caller: 'cron',
    }),
  document_reconcile: defineAdminRoute({
      path: '/api/cron/document-reconcile',
      params: {},
      methods: { GET: CRON },
      caller: 'cron',
    }),
  // BL-2788 (ADR 074 — Upload Standard § 3): deletes expired staged and released
  // uploads, files first then rows.
  uploads_janitor: defineAdminRoute({
      path: '/api/cron/uploads-janitor',
      params: {},
      methods: { GET: { ...CRON, response: responseType<UploadsJanitorResponse>() } },
      caller: 'cron',
    }),
  notifications_drain: defineAdminRoute({
      path: '/api/cron/notifications/drain',
      params: {},
      methods: {
        POST: {
          grant: { none: 'cron — the CRON_SECRET bearer or a cron-invoker OIDC token runs the sweep, and a Cloud Tasks OIDC token drains a channel' },
          tenant: CRON_TENANT,
          response: responseType<NotificationDrainResponse>(),
        },
      },
      caller: 'cron',
    }),
  // BL-1520: reap_test_accounts retired — the pattern-matching seed reaper is
  // replaced by wipe_dev_data below, which deletes everything for an explicit
  // tenant allowlist instead of recognising seed data by name.
  wipe_dev_data: defineAdminRoute({
      path: '/api/cron/wipe-dev-data',
      params: {},
      methods: {
        GET: {
          grant: { none: 'cron — the DEV_WIPE_SECRET bearer or a cron-wipe-invoker OIDC token authorizes it, on allowlisted dev environments only' },
          tenant: { from: 'none', reason: 'cron — wipes the allowlisted dev tenants' },
        },
      },
      caller: 'cron',
    }),
  // BL-2378: nightly. Asks Finix about processor entries pending over two
  // days and re-reads each open PCI form; stamps through the ledger's one
  // processor effect (ADR 067 — Processor Contract § 4).
  payments_sweep: defineAdminRoute({
      path: '/api/cron/payments-sweep',
      params: {},
      methods: { GET: { ...CRON, response: responseType<PaymentsSweepResponse>() } },
      caller: 'cron',
    }),
  refresh_payment_holds: defineAdminRoute({
      path: '/api/cron/refresh-payment-holds',
      params: {},
      methods: { GET: CRON },
      caller: 'cron',
    }),
  seller_day_before: defineAdminRoute({
      path: '/api/cron/seller-day-before',
      params: {},
      methods: { GET: CRON },
      caller: 'cron',
    }),
  // BL-659: staff_new_registration cron retired — staff are notified at the
  // source when a bidder self-registers (bidder-web selfRegister enqueue).
  timed_auction_open: defineAdminRoute({
      path: '/api/cron/timed-auction-open',
      params: {},
      methods: { GET: CRON, POST: CRON },
      caller: 'cron',
    }),
  close_due_timed_lots: defineAdminRoute({
      path: '/api/cron/close-due-timed-lots',
      params: {},
      methods: { GET: CRON, POST: CRON },
      caller: 'cron',
    }),
};
